import { isAuditType, type StatsTimePeriod, type AlertAudience, type AlertCategory } from "@/services/api/guardiansv2/types";
import type { AlertAudienceFilter, AlertCategoryFilter, GuardiansFilterValues } from "./shared/GuardiansFilterBar";
import type { AuditBucket } from "./audits/useAudits";
import { isScreeningBucket, type ScreeningBucket } from "./screenings/useScreenings";
export type { ScreeningBucket };

/** Outcome bucket tokens that may appear on legacy ``screening`` query params. */
export const SCREENING_BUCKET_VALUES = ["all", "passed", "flagged", "failed"] as const satisfies readonly ScreeningBucket[];

const MONGO_OBJECT_ID = /^[a-f0-9]{24}$/i;
const SCREENING_DOCUMENT_ID = /^[a-zA-Z0-9_-]+$/;

export function isScreeningDocumentId(value: string | null): boolean {
  if (value == null) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (isScreeningBucket(trimmed)) return false;
  if (MONGO_OBJECT_ID.test(trimmed)) return true;
  return SCREENING_DOCUMENT_ID.test(trimmed);
}

export function normalizeGuardiansScreeningParam(screening: string | null): {
  screeningId: string | null;
  screeningOutcomePatch: string | null;
} {
  if (screening == null) {
    return { screeningId: null, screeningOutcomePatch: null };
  }
  const trimmed = screening.trim();
  if (!trimmed) {
    return { screeningId: null, screeningOutcomePatch: null };
  }
  if (trimmed === "passed" || trimmed === "flagged" || trimmed === "failed") {
    return { screeningId: null, screeningOutcomePatch: trimmed };
  }
  if (isScreeningDocumentId(trimmed)) {
    return { screeningId: trimmed, screeningOutcomePatch: null };
  }
  return { screeningId: null, screeningOutcomePatch: null };
}

export type GuardiansView = "alerts" | "audits" | "screenings" | "rules" | "conversation" | "stats";
export type GuardiansPanelMode = "entity" | "audit" | "both";

/** Search params mirrored on the aniOS ``ops-guardians`` window for share / URL sync. */
export const GUARDIANS_OS_PARAM_KEYS = [
  "view",
  "mission",
  "mission_template",
  "task",
  "audit",
  "alert",
  "agent",
  "catalogue",
  "catalogue_key",
  "rule_id",
  "audit_origin",
  "audit_type",
  "audits_mode",
  "screening",
  "screening_outcome",
  "rating",
  "settings",
  "conversation",
  "time_period",
  "category",
  "audience",
] as const;

/** Extra OS window keys mirrored into Guardians search params (not in share URL helpers). */
export const GUARDIANS_OS_EXTRA_PARAM_KEYS = ["interaction"] as const;

export type GuardiansOsParamKey = (typeof GUARDIANS_OS_PARAM_KEYS)[number];

const GUARDIANS_VIEWS: readonly GuardiansView[] = [
  "alerts",
  "audits",
  "screenings",
  "rules",
  "conversation",
  "stats",
];
const PANEL_MODES: readonly GuardiansPanelMode[] = ["entity", "audit", "both"];

function isGuardiansView(value: string): value is GuardiansView {
  return (GUARDIANS_VIEWS as readonly string[]).includes(value);
}

function isPanelMode(value: string): value is GuardiansPanelMode {
  return (PANEL_MODES as readonly string[]).includes(value);
}

export function parseGuardiansView(raw: string | null): GuardiansView {
  if (raw && isGuardiansView(raw)) return raw;
  return "alerts";
}

/** Resolve view from URL, including legacy ``view=audits&audits_mode=screenings``. */
export function resolveGuardiansView(
  viewRaw: string | null,
  auditsModeRaw: string | null
): GuardiansView {
  if (viewRaw === "audits" && auditsModeRaw === "screenings") return "screenings";
  return parseGuardiansView(viewRaw);
}

export function parsePanelMode(raw: string | null): GuardiansPanelMode | null {
  if (raw && isPanelMode(raw)) return raw;
  return null;
}

export type AuditOriginFilter = "all" | "alert";

export function parseAuditOriginFilter(raw: string | null): AuditOriginFilter {
  return raw === "alert" ? "alert" : "all";
}

export type AuditsWorkbenchMode = "audits" | "screenings";

export function parseAuditsWorkbenchMode(raw: string | null): AuditsWorkbenchMode {
  return raw === "screenings" ? "screenings" : "audits";
}

export function parseScreeningBucket(raw: string | null): ScreeningBucket {
  if (raw === "passed" || raw === "flagged" || raw === "failed") return raw;
  return "all";
}

export function parseAuditBucket(raw: string | null): AuditBucket {
  if (
    raw === "red" ||
    raw === "amber" ||
    raw === "green" ||
    raw === "failed"
  ) {
    return raw;
  }
  return "all";
}

const STATS_TIME_PERIODS: readonly StatsTimePeriod[] = ["1w", "1m", "3m", "all"];

export function parseStatsTimePeriod(raw: string | null): StatsTimePeriod {
  if (raw && (STATS_TIME_PERIODS as readonly string[]).includes(raw)) {
    return raw as StatsTimePeriod;
  }
  return "1m";
}

const ALERT_CATEGORIES: readonly AlertCategory[] = [
  "rule_check",
  "tool_error",
  "task_error",
  "mission_error",
  "trigger_error",
  "runner_error",
  "low_confidence",
] as const;

const ALERT_AUDIENCES: readonly AlertAudience[] = ["engineer", "ops", "adviser"] as const;

function isAlertCategory(value: string): value is AlertCategory {
  return (ALERT_CATEGORIES as readonly string[]).includes(value);
}

function isAlertAudience(value: string): value is AlertAudience {
  return (ALERT_AUDIENCES as readonly string[]).includes(value);
}

export function parseAlertCategoryFilter(raw: string | null): AlertCategoryFilter {
  if (raw && isAlertCategory(raw)) return raw;
  return "all";
}

export function parseAlertAudienceFilter(raw: string | null): AlertAudienceFilter {
  if (raw && isAlertAudience(raw)) return raw;
  return "all";
}

export function filtersFromSearchParams(params: URLSearchParams): GuardiansFilterValues {
  const auditType = params.get("audit_type");
  return {
    auditType:
      auditType && auditType !== "all" && isAuditType(auditType) ? auditType : "all",
    missionTemplateId: params.get("mission_template") ?? "",
    taskId: params.get("task") ?? "",
    agentId: params.get("agent") ?? "",
    catalogueId: params.get("catalogue") ?? "",
    alertCategory: parseAlertCategoryFilter(params.get("category")),
    alertAudience: parseAlertAudienceFilter(params.get("audience")),
  };
}

export function patchSearchParams(
  prev: URLSearchParams,
  patch: Record<string, string | null>
): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const [key, value] of Object.entries(patch)) {
    if (value == null || value === "") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
  }
  return next;
}

/** Build a search-param patch from aniOS window params when opening a deep link. */
export function guardiansSearchParamsPatchFromOsParams(
  params: Record<string, string>
): Record<string, string | null> | null {
  const patch: Record<string, string | null> = {};
  let hasAny = false;
  for (const key of [...GUARDIANS_OS_PARAM_KEYS, ...GUARDIANS_OS_EXTRA_PARAM_KEYS]) {
    const value = params[key];
    if (value) {
      patch[key] = value;
      hasAny = true;
    }
  }
  return hasAny ? patch : null;
}

/** Serialize aniOS window params to URL search params for `/ops/guardians`. */
export function guardiansSearchParamsFromOsParams(params: Record<string, string>): URLSearchParams {
  const patch: Record<string, string | null> = {};
  for (const key of [...GUARDIANS_OS_PARAM_KEYS, ...GUARDIANS_OS_EXTRA_PARAM_KEYS]) {
    const value = params[key];
    if (value) patch[key] = value;
  }
  return patchSearchParams(new URLSearchParams(), patch);
}
